import type { DimensionOverrides } from '@rogue/rackbuilder'
import type { ProductMeasurementConfig } from '../../RackTypes'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'

// The model is exported at true meters while static lines convert at 38 per unit, so the bounding box would read about 3.5% short.
// Lengths are the published spec; widths are measured off the model because the published 28" only holds for the closed frame.
const TRAP_BAR_WIDTH = { closed: 28, open: 25.7 }
const TRAP_BAR_LENGTH = { rackable: 83.5, nonRackable: 74 }

function getSelectedFrame(buyRequest: BuyRequest, products: Record<string, Product>): Product | undefined {
  let bundleBR = buyRequest
  let bundle = products[String(bundleBR.productId)]

  while (bundle && bundle.type !== 'bundle' && bundleBR.children?.[0]) {
    bundleBR = bundleBR.children[0]
    bundle = products[String(bundleBR.productId)]
  }

  const frameOptionId = bundle?.bundleOptions?.find(opt => String(opt.title ?? '').toLowerCase().includes('frame'))?.id
  if (frameOptionId === undefined) return undefined

  let frameBR = bundleBR.children?.find(child => child.metadata?.bundleOptionId === frameOptionId && child.qty > 0)
  if (!frameBR) return undefined

  while (frameBR.children?.length) frameBR = frameBR.children[0]

  return products[String(frameBR.productId)]
}

function getTrapBarDimensionOverrides(buyRequest: BuyRequest, products: Record<string, Product>): DimensionOverrides | null {
  const frame = getSelectedFrame(buyRequest, products)
  if (!frame) return null

  // Same readings as createTrapBar, so the labels always describe the frame and sleeve that are rendered.
  const open = String(frame.attributes?.frame_type ?? '').toLowerCase() === 'open'
  const rackable = String(frame.attributes?.bar_sleeve_design ?? '').toLowerCase() !== 'non-rackable'

  return {
    width : open ? TRAP_BAR_WIDTH.open : TRAP_BAR_WIDTH.closed,
    depth : rackable ? TRAP_BAR_LENGTH.rackable : TRAP_BAR_LENGTH.nonRackable,
  }
}

export const TRAP_BAR_MEASUREMENT_CONFIG: ProductMeasurementConfig = {
  dynamic               : false,
  // Only frames the overhead view; leaving it returns to the camera.defaultZoom set in getPart.
  defaultZoomLevel      : 1.5,
  // Lines through the middle of the bar's height; the engine's default drop would put them about 16" under a bar that lies flat.
  verticalPosition      : 0,
  verticalOffset        : 0,
  getDimensionOverrides : getTrapBarDimensionOverrides,
}
